import { Logo } from "@koaform/ui";
import { useEffect, useState } from "react";

import { usePageTitle } from "../hooks/usePageTitle.js";
import { api, ApiError, type AdminUser, type AdminOverview } from "../lib/api.js"; // prettier-ignore
import { controlCls } from "../ui/Field.js";

const RENDERER_URL =
  import.meta.env.VITE_RENDERER_URL ?? "http://localhost:5052";

const when = (iso: string | null) => {
  if (!iso) return "never";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 60) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

const Stat = ({ label, value }: { label: string; value: number | string }) => (
  <div className="rounded-[14px] border border-line-strong bg-surface px-4 py-3">
    <div className="font-mono text-[10px] tracking-[0.1em] text-faint uppercase">
      {label}
    </div>
    <div className="mt-1 text-[22px] font-extrabold tracking-[-0.02em] text-ink">
      {value}
    </div>
  </div>
);

/**
 * Operator console.
 *
 * One search box and one screen. Koalendar's equivalent spreads an account
 * across several pages, which means answering "why can this person not create
 * a form" takes four clicks and some arithmetic. Here the limits table shows
 * the default, the override and what is actually in force side by side, so
 * the answer is visible without doing the sum in your head.
 */
export const Admin = () => {
  usePageTitle("Admin");
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [email, setEmail] = useState("");
  const [result, setResult] = useState<AdminUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);

  useEffect(() => {
    api
      .adminOverview()
      .then(setOverview)
      .catch(() => setOverview(null));
  }, []);

  const search = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await api.adminFindUser(email.trim()));
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not look that up.",
      );
    } finally {
      setBusy(false);
    }
  };

  const patch = async (body: Parameters<typeof api.adminUpdateUser>[1]) => {
    if (!result) return;
    setBusy(true);
    try {
      await api.adminUpdateUser(result.user.id, body);
      setResult(await api.adminFindUser(result.user.email));
      setSaved("Saved");
      setTimeout(() => setSaved(null), 1600);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-dvh bg-paper">
      <header className="border-b border-line-strong bg-surface">
        <div className="mx-auto flex max-w-[1120px] items-center justify-between px-8 py-4">
          <a href="/" aria-label="koaform">
            <Logo size={28} />
          </a>
          <span className="rounded-full bg-amber-100 px-3 py-1 text-[12px] font-bold text-amber-800">
            Admin
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-[1120px] px-8 py-10">
        {overview ? (
          <div className="mb-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label="Users" value={overview.users.users} />
            <Stat label="New 24h" value={overview.users.last24h} />
            <Stat label="New 7d" value={overview.users.last7d} />
            <Stat label="Forms" value={overview.forms.forms} />
            <Stat label="Live" value={overview.forms.published} />
            <Stat label="Responses" value={overview.responses.responses} />
          </div>
        ) : null}

        <form onSubmit={search} className="mb-8 flex max-w-[560px] gap-2">
          <input
            autoFocus
            type="search"
            value={email}
            placeholder="Find an account by email"
            onChange={(e) => setEmail(e.target.value)}
            className={`${controlCls} mb-0`}
          />
          <button
            type="submit"
            disabled={busy}
            className="flex-none rounded-[10px] bg-primary-600 px-5 text-[15px] font-bold text-white hover:bg-primary-700 disabled:bg-line-bold"
          >
            {busy ? "…" : "Find"}
          </button>
        </form>

        {error ? (
          <p role="alert" className="mb-6 text-[14px] text-danger">
            {error}
          </p>
        ) : null}

        {!result && overview?.recentSignups.length ? (
          <div>
            <h2 className="mb-3 font-mono text-[10px] tracking-[0.1em] text-faint">
              RECENT SIGNUPS
            </h2>
            <ul className="list-none rounded-[14px] border border-line-strong bg-surface p-0">
              {overview.recentSignups.map((s) => (
                <li
                  key={s.email}
                  className="flex items-center justify-between border-b border-line px-4 py-2.5 last:border-0"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setEmail(s.email);
                      void api.adminFindUser(s.email).then(setResult);
                    }}
                    className="font-mono text-[13px] text-body hover:text-primary-600"
                  >
                    {s.email}
                  </button>
                  <span className="text-[12.5px] text-faint">
                    {when(s.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {result ? (
          <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
            <div>
              <div className="mb-4 rounded-[16px] border border-line-strong bg-surface p-5">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="m-0 text-[19px] font-extrabold tracking-[-0.02em]">
                    {result.user.name || result.user.email}
                  </h2>
                  {result.user.isBanned ? (
                    <span className="rounded-full bg-danger/10 px-2.5 py-1 text-[11px] font-bold text-danger">
                      Banned
                    </span>
                  ) : null}
                  <span className="rounded-full bg-line px-2.5 py-1 text-[11px] font-semibold text-body">
                    via {result.user.signedUpWith}
                  </span>
                </div>
                {result.user.name ? (
                  <p className="mt-1 mb-3 font-mono text-[13px] text-muted">
                    {result.user.email}
                  </p>
                ) : (
                  <div className="mb-3" />
                )}
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-[13px] text-muted">
                  <span>Joined {when(result.user.createdAt)}</span>
                  <span>Last seen {when(result.user.lastLoginAt)}</span>
                  <span>
                    {result.totals.forms} forms · {result.totals.published} live
                    · {result.totals.responses} responses
                  </span>
                </div>
              </div>

              <h3 className="mb-2 font-mono text-[10px] tracking-[0.1em] text-faint">
                FORMS
              </h3>
              <ul className="list-none rounded-[14px] border border-line-strong bg-surface p-0">
                {result.forms.length === 0 ? (
                  <li className="px-4 py-6 text-center text-[13.5px] text-faint">
                    No forms yet.
                  </li>
                ) : (
                  result.forms.map((f) => (
                    <li
                      key={f.id}
                      className="flex items-center justify-between gap-3 border-b border-line px-4 py-2.5 last:border-0"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-[14px] font-semibold text-ink">
                          {f.name ?? f.title}
                        </div>
                        <div className="font-mono text-[11px] text-faint">
                          {f.status} · {f.responsesCount} responses ·{" "}
                          {when(f.updatedAt)}
                        </div>
                      </div>
                      {f.slug ? (
                        <a
                          href={`${RENDERER_URL}/r/${f.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex-none text-[12.5px] font-semibold"
                        >
                          Open
                        </a>
                      ) : null}
                    </li>
                  ))
                )}
              </ul>
            </div>

            <div>
              <h3 className="mb-2 font-mono text-[10px] tracking-[0.1em] text-faint">
                LIMITS
              </h3>
              {/*
                Default, override and effective in one row. The question this
                screen exists to answer is "why can this person not do X", and
                that is unanswerable if you can only see one of the three.
              */}
              <div className="mb-4 rounded-[14px] border border-line-strong bg-surface p-1">
                {Object.entries(result.defaults).map(([key, def]) => {
                  const override = result.user.limits[key];
                  const effective = result.effectiveLimits[key];
                  return (
                    <div
                      key={key}
                      className="flex items-center justify-between gap-2 border-b border-line px-3 py-2 last:border-0"
                    >
                      <span
                        className="truncate text-[12.5px] text-body"
                        title={key}
                      >
                        {key}
                      </span>
                      <span className="flex flex-none items-center gap-2">
                        <span
                          className={`font-mono text-[12px] ${override === undefined ? "text-faint" : "text-primary-600"}`}
                        >
                          {effective}
                        </span>
                        <input
                          // Re-keyed on the saved value so the box reflects a
                          // cleared or changed override after the refetch —
                          // an uncontrolled input would keep the stale one.
                          key={String(override)}
                          type="number"
                          min={0}
                          defaultValue={override ?? ""}
                          placeholder={String(def)}
                          title={`Default ${def}. Blank to remove the override.`}
                          onBlur={(e) => {
                            const raw = e.target.value.trim();
                            // Blank clears the override rather than setting 0.
                            const next = raw === "" ? null : Number(raw);
                            if (next === (override ?? null)) return;
                            void patch({ limits: { [key]: next } });
                          }}
                          className="w-20 rounded-lg border border-line-input px-2 py-1 text-right font-mono text-[12px] focus:border-primary-600 focus:outline-none"
                        />
                      </span>
                    </div>
                  );
                })}
              </div>
              {saved ? (
                <p className="mb-3 text-[12.5px] font-semibold text-primary-600">
                  {saved}
                </p>
              ) : null}

              <h3 className="mb-2 font-mono text-[10px] tracking-[0.1em] text-faint">
                ACTIONS
              </h3>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  const ban = !result.user.isBanned;
                  if (
                    ban &&
                    !window.confirm(
                      `Ban ${result.user.email}?\n\nThey keep their data but cannot sign in.`,
                    )
                  ) {
                    return;
                  }
                  void patch({ isBanned: ban });
                }}
                className={`w-full rounded-[10px] border px-4 py-2.5 text-sm font-semibold ${
                  result.user.isBanned
                    ? "border-line-bold bg-surface text-ink hover:border-primary-600"
                    : "border-danger/30 bg-surface text-danger hover:border-danger"
                }`}
              >
                {result.user.isBanned ? "Unban account" : "Ban account"}
              </button>
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
};
